import axios from "axios";
import type { AxiosInstance } from "axios";
import { defineNuxtPlugin } from "#app";
import AuthModule from "~~/repository/modules/auth";
import { ENDPOINTS } from "~/repository/endpoints";
import type { LoginResponse } from "~/types/auth";
import {
  beginRefresh,
  clearViewAsBackupSession,
  clearViewAsState,
  createAuthenticationFailureError,
  enqueueRetryRequest,
  getAccessToken,
  getRefreshToken,
  getViewAsBackupSession,
  hasActiveRefresh,
  incrementRetryCount,
  isViewAsActive,
  isAuthenticationFailureError,
  markExpectedUnauthorizedError,
  replayRetryQueue,
  rejectRetryQueue,
  restorePrimaryAuthSession,
  setAuthTokens,
  shouldAttachAuthorization,
  shouldRetryAfterUnauthorized,
  type InternalAuthRequestConfig,
} from "~/repository/authSession";
import ProfileModule from "~/repository/modules/profile";
import GamificationModule from "~/repository/modules/gamification";
import ContentModule from "~/repository/modules/content";
import ClassroomModule from "~/repository/modules/classroom";
import AssessmentModule from "~/repository/modules/assessment";
import SchoolAssessmentsModule from "~/repository/modules/schoolAssessments";
import PublicAssessmentModule from "~/repository/modules/publicAssessment";
import DocumentsModule from "~/repository/modules/documents";
import SubscriptionModule from "~/repository/modules/subscription";
import BrazilAPI from "~/repository/modules/brazilAPI";
import PaymentModule from "~/repository/modules/payment";
import FinanceModule from "~/repository/modules/finance";
import SchoolFinanceModule from "~/repository/modules/schoolFinance";
import FinanceReceivingModule from "~/repository/modules/financeReceiving";
import CalendarModule from "~/repository/modules/calendar";
import OrganizationsModule from "~/repository/modules/organizations";
import EditorModule from "~/repository/modules/editor";
import LessonIssueReportsModule from "~/repository/modules/lessonIssueReports";
import { useAuthStore } from "~/stores/auth";
import AdminAuth from "~/repository/modules/admin/auth";
import {
  AdminPagarmeChargesModule,
  AdminPagarmeCyclesModule,
  AdminPagarmeInvoicesModule,
  AdminPagarmeSubscriptionsModule,
} from "~/repository/modules/admin/pagarme";
import AdminPaymentModule from "~/repository/modules/admin/payment";
import {
  AdminPaymentStatisticsModule,
  AdminPaymentSubscriptionsModule,
} from "~/repository/modules/admin/payments";
import { AdminAssessmentModule } from "~/repository/modules/admin/assessment";
import AdminAIModule from "~/repository/modules/admin/ai";
import AdminDocumentsModule from "~/repository/modules/admin/documents";
import AdminEntitlementsModule from "~/repository/modules/admin/entitlements";
import { AdminProfilesStatisticsModule } from "~/repository/modules/admin/profiles";
import AdminBackgroundJobsModule from "~/repository/modules/admin/backgroundJobs";
import AdminContentQualityModule from "~/repository/modules/admin/contentQuality";
import AdminStorageAuditModule from "~/repository/modules/admin/storageAudit";
import AdminVideoCallsModule from "~/repository/modules/admin/videoCalls";
import AdminNotificationsModule from "~/repository/modules/admin/notifications";
import AdminOrganizationsModule from "~/repository/modules/admin/organizations";
import AdminCampaignsModule from "~/repository/modules/admin/campaigns";
import AdminManagedLinksModule from "~/repository/modules/admin/managedLinks";
import AdminOutreachModule from "~/repository/modules/admin/outreach";
import {
  AdminContentLessonPlanCollectionsModule,
  AdminContentLessonPlansModule,
  AdminContentLessonsModule,
} from "~/repository/modules/admin/content";
import { AdminUserAccessConfigModule } from "~/repository/modules/admin/userAccessConfig";
import AdminStudentModule from "~/repository/modules/admin/student";
import { TeacherPaymentSubscriptionModule } from "~/repository/modules/teacher/payment";
import { TeacherProfileFavoritesModule } from "~/repository/modules/teacher/profile";
import ReferralsModule from "~/repository/modules/referrals";
import AdminReferralsModule from "~/repository/modules/admin/referrals";
import VideoCallsModule from "~/repository/modules/videoCalls";
import AttendanceModule from "~/repository/modules/attendance";
import SchoolManagementModule from "~/repository/modules/schoolManagement";
import SchoolInterestsModule from "~/repository/modules/schoolInterests";
import SchoolFormsModule from "~/repository/modules/schoolForms";
import PublicSchoolFormsModule from "~/repository/modules/publicSchoolForms";
import StudentRequestsModule from "~/repository/modules/studentRequests";
import StudentAgreementsModule from "~/repository/modules/studentAgreements";
import SchoolNotificationsModule from "~/repository/modules/schoolNotifications";
import SchoolCommunicationsModule from "~/repository/modules/schoolCommunications";
import SchoolInboxModule from "~/repository/modules/schoolInbox";
import SchoolStoreModule from "~/repository/modules/schoolStore";
import AdminSchoolStoresModule from "~/repository/modules/admin/schoolStores";
import StoreMaintenanceModule from "~/repository/modules/storeMaintenance";
import {
  PublicSchedulingModule,
  SchedulingManagementModule,
} from "~/repository/modules/scheduling";

/** ApiInstance interface provides us with good typing */
export interface ApiInstance {
  admin: {
    auth: AdminAuth;
    ai: AdminAIModule;
    backgroundJobs: AdminBackgroundJobsModule;
    contentQuality: AdminContentQualityModule;
    storageAudit: AdminStorageAuditModule;
    videoCalls: AdminVideoCallsModule;
    notifications: AdminNotificationsModule;
    organizations: AdminOrganizationsModule;
    schoolStores: AdminSchoolStoresModule;
    campaigns: AdminCampaignsModule;
    managedLinks: AdminManagedLinksModule;
    outreach: AdminOutreachModule;
    scheduling: SchedulingManagementModule;
    userAccessConfig: AdminUserAccessConfigModule;
    student: AdminStudentModule;
    payment: AdminPaymentModule;
    assessment: AdminAssessmentModule;
    documents: AdminDocumentsModule;
    entitlements: AdminEntitlementsModule;
    referrals: AdminReferralsModule;
    payments: {
      subscriptions: AdminPaymentSubscriptionsModule;
      statistics: AdminPaymentStatisticsModule;
    };
    content: {
      lessons: AdminContentLessonsModule;
      lessonPlans: AdminContentLessonPlansModule;
      lessonPlanCollections: AdminContentLessonPlanCollectionsModule;
    };
    profiles: {
      statistics: AdminProfilesStatisticsModule;
    };
    pagarme: {
      invoices: AdminPagarmeInvoicesModule;
      charges: AdminPagarmeChargesModule;
      cycles: AdminPagarmeCyclesModule;
      subscriptions: AdminPagarmeSubscriptionsModule;
    };
  };
  teacher: {
    payment: {
      subscriptions: TeacherPaymentSubscriptionModule;
    };
    profile: {
      favorites: TeacherProfileFavoritesModule;
    };
  };
  auth: AuthModule;
  profile: ProfileModule;
  gamification: GamificationModule;
  organizations: OrganizationsModule;
  content: ContentModule;
  documents: DocumentsModule;
  assessment: AssessmentModule;
  schoolAssessments: SchoolAssessmentsModule;
  publicAssessment: PublicAssessmentModule;
  editor: EditorModule;
  lessonIssueReports: LessonIssueReportsModule;
  finance: FinanceModule;
  schoolFinance: SchoolFinanceModule;
  financeReceiving: FinanceReceivingModule;
  referrals: ReferralsModule;
  calendar: CalendarModule;
  classroom: ClassroomModule;
  videoCalls: VideoCallsModule;
  attendance: AttendanceModule;
  schoolManagement: SchoolManagementModule;
  schoolInterests: SchoolInterestsModule;
  schoolForms: SchoolFormsModule;
  publicSchoolForms: PublicSchoolFormsModule;
  studentRequests: StudentRequestsModule;
  studentAgreements: StudentAgreementsModule;
  schoolNotifications: SchoolNotificationsModule;
  schoolCommunications: SchoolCommunicationsModule;
  schoolInbox: SchoolInboxModule;
  schoolStore: SchoolStoreModule;
  storeMaintenance: StoreMaintenanceModule;
  publicScheduling: PublicSchedulingModule;
  schoolScheduling: SchedulingManagementModule;
  subscription: SubscriptionModule;
  payment: PaymentModule;
  brazilAPI: BrazilAPI;
}

const tryRestoreAdminViewAsSession = async (
  authStore: ReturnType<typeof useAuthStore>,
) => {
  if (!isViewAsActive()) {
    return false;
  }

  const backupSession = getViewAsBackupSession();
  if (!backupSession) {
    return false;
  }

  restorePrimaryAuthSession(backupSession);
  clearViewAsBackupSession();
  clearViewAsState();

  try {
    await authStore.fetchUserData();
    await navigateTo(backupSession.originalPath || "/admin-dashboard/home");
    return true;
  } catch {
    return false;
  }
};

const callAuthenticationFailed = async (error: unknown) => {
  markExpectedUnauthorizedError(error);
  const authStore = useAuthStore();
  const restored = await tryRestoreAdminViewAsSession(authStore);
  if (restored) {
    return Promise.reject(error);
  }
  authStore.logout();
  await navigateTo("/login");
  return new Promise<never>(() => {
    // Authentication failed and the route changed to login; leave the abandoned request unresolved.
  });
};

const refreshToken = async (axiosInstance: AxiosInstance) => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw createAuthenticationFailureError("Refresh token ausente.");
  }

  const response = await axiosInstance.post<LoginResponse>(
    ENDPOINTS.AUTH.REFRESH,
    {
      refresh: refreshToken,
    },
  );
  const { access, refresh } = response.data ?? {};

  if (!access || !refresh) {
    throw createAuthenticationFailureError(
      "Resposta de refresh sem access/refresh.",
    );
  }

  setAuthTokens({ access, refresh });
};

export default defineNuxtPlugin((nuxtApp) => {
  const baseUrl = nuxtApp.$config.public.TEACHPLUS_BASE_URL;
  const axiosInstance = axios.create({
    baseURL: baseUrl,
  });
  const refreshAxiosInstance = axios.create({
    baseURL: baseUrl,
  });

  axiosInstance.interceptors.request.use(
    (config) => {
      const authConfig = config as InternalAuthRequestConfig;

      if (!shouldAttachAuthorization(authConfig)) {
        return authConfig;
      }

      const accessToken = getAccessToken();

      if (accessToken) {
        authConfig.headers.Authorization = `JWT ${accessToken}`;
      }

      return authConfig;
    },
    undefined,
    {
      synchronous: true,
    },
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      if (!axios.isAxiosError(error) || error.response?.status !== 401) {
        return Promise.reject(error);
      }

      const originalRequest = error.config as
        | InternalAuthRequestConfig
        | undefined;

      if (!originalRequest || !shouldAttachAuthorization(originalRequest)) {
        return Promise.reject(error);
      }

      markExpectedUnauthorizedError(error);

      if (!shouldRetryAfterUnauthorized(originalRequest)) {
        if (originalRequest.suppressAuthFailureRedirect === true) {
          return Promise.reject(error);
        }
        return callAuthenticationFailed(error);
      }

      incrementRetryCount(originalRequest);

      if (hasActiveRefresh()) {
        return enqueueRetryRequest(() =>
          axiosInstance.request(originalRequest),
        );
      }

      const currentRefresh = beginRefresh(refreshToken(refreshAxiosInstance));

      try {
        await currentRefresh;
        await replayRetryQueue();
        return axiosInstance.request(originalRequest);
      } catch (refreshError) {
        rejectRetryQueue(refreshError);

        if (isAuthenticationFailureError(refreshError)) {
          if (originalRequest.suppressAuthFailureRedirect === true) {
            return Promise.reject(refreshError);
          }
          return callAuthenticationFailed(refreshError);
        }

        return Promise.reject(refreshError);
      }
    },
  );

  const modules: ApiInstance = {
    admin: {
      auth: new AdminAuth(axiosInstance),
      ai: new AdminAIModule(axiosInstance),
      backgroundJobs: new AdminBackgroundJobsModule(axiosInstance),
      contentQuality: new AdminContentQualityModule(axiosInstance),
      storageAudit: new AdminStorageAuditModule(axiosInstance),
      videoCalls: new AdminVideoCallsModule(axiosInstance),
      notifications: new AdminNotificationsModule(axiosInstance),
      organizations: new AdminOrganizationsModule(axiosInstance),
      schoolStores: new AdminSchoolStoresModule(axiosInstance),
      campaigns: new AdminCampaignsModule(axiosInstance),
      managedLinks: new AdminManagedLinksModule(axiosInstance),
      outreach: new AdminOutreachModule(axiosInstance),
      scheduling: new SchedulingManagementModule(
        axiosInstance,
        "/api/admin/scheduling/",
      ),
      userAccessConfig: new AdminUserAccessConfigModule(axiosInstance),
      student: new AdminStudentModule(axiosInstance),
      payment: new AdminPaymentModule(axiosInstance),
      assessment: new AdminAssessmentModule(axiosInstance),
      documents: new AdminDocumentsModule(axiosInstance),
      entitlements: new AdminEntitlementsModule(axiosInstance),
      referrals: new AdminReferralsModule(axiosInstance),
      payments: {
        subscriptions: new AdminPaymentSubscriptionsModule(axiosInstance),
        statistics: new AdminPaymentStatisticsModule(axiosInstance),
      },
      content: {
        lessons: new AdminContentLessonsModule(axiosInstance),
        lessonPlans: new AdminContentLessonPlansModule(axiosInstance),
        lessonPlanCollections: new AdminContentLessonPlanCollectionsModule(
          axiosInstance,
        ),
      },
      profiles: {
        statistics: new AdminProfilesStatisticsModule(axiosInstance),
      },
      pagarme: {
        invoices: new AdminPagarmeInvoicesModule(axiosInstance),
        charges: new AdminPagarmeChargesModule(axiosInstance),
        cycles: new AdminPagarmeCyclesModule(axiosInstance),
        subscriptions: new AdminPagarmeSubscriptionsModule(axiosInstance),
      },
    },
    teacher: {
      payment: {
        subscriptions: new TeacherPaymentSubscriptionModule(axiosInstance),
      },
      profile: {
        favorites: new TeacherProfileFavoritesModule(axiosInstance),
      },
    },
    auth: new AuthModule(axiosInstance),
    profile: new ProfileModule(axiosInstance),
    gamification: new GamificationModule(axiosInstance),
    organizations: new OrganizationsModule(axiosInstance),
    content: new ContentModule(axiosInstance),
    documents: new DocumentsModule(axiosInstance),
    assessment: new AssessmentModule(axiosInstance),
    schoolAssessments: new SchoolAssessmentsModule(axiosInstance),
    publicAssessment: new PublicAssessmentModule(axiosInstance),
    editor: new EditorModule(axiosInstance),
    lessonIssueReports: new LessonIssueReportsModule(axiosInstance),
    finance: new FinanceModule(axiosInstance),
    schoolFinance: new SchoolFinanceModule(axiosInstance),
    financeReceiving: new FinanceReceivingModule(axiosInstance),
    referrals: new ReferralsModule(axiosInstance),
    calendar: new CalendarModule(axiosInstance),
    classroom: new ClassroomModule(axiosInstance),
    videoCalls: new VideoCallsModule(axiosInstance),
    attendance: new AttendanceModule(axiosInstance),
    schoolManagement: new SchoolManagementModule(axiosInstance),
    schoolInterests: new SchoolInterestsModule(axiosInstance),
    schoolForms: new SchoolFormsModule(axiosInstance),
    publicSchoolForms: new PublicSchoolFormsModule(axiosInstance),
    studentRequests: new StudentRequestsModule(axiosInstance),
    studentAgreements: new StudentAgreementsModule(axiosInstance),
    schoolNotifications: new SchoolNotificationsModule(axiosInstance),
    schoolCommunications: new SchoolCommunicationsModule(axiosInstance),
    schoolInbox: new SchoolInboxModule(axiosInstance),
    schoolStore: new SchoolStoreModule(axiosInstance),
    storeMaintenance: new StoreMaintenanceModule(axiosInstance),
    publicScheduling: new PublicSchedulingModule(axiosInstance),
    schoolScheduling: new SchedulingManagementModule(
      axiosInstance,
      "/api/organizations/current/scheduling/",
    ),
    subscription: new SubscriptionModule(axiosInstance),
    payment: new PaymentModule(axiosInstance),
    brazilAPI: new BrazilAPI(axiosInstance),
  };

  return {
    provide: {
      api: modules,
    },
  };
});
